import React, { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function fmtUnlockDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

// Gate shown when a member tries to start a session without an available
// entitlement. Reasons: no_code (redeem), exhausted (12 used — contact +
// redeem new), weekly_lock (show unlock date).
export default function EntitlementModal({ gate, onClose, onProceed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [redeemed, setRedeemed] = useState(null);

  if (!gate) return null;
  const { reason, next_available_at } = gate;
  const showRedeem = reason === "no_code" || reason === "exhausted";

  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/me/redeem-code`, { code: code.trim() });
      setRedeemed(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  let title = "One session per week";
  let body = `Your next session unlocks on ${fmtUnlockDate(next_available_at)}. Weekly pacing is part of the program — it gives your nervous system time to integrate.`;
  if (reason === "no_code") {
    title = "Redeem your access code";
    body = "Enter the access code the wellness team gave you after payment. Each code unlocks 12 sessions, one per week.";
  } else if (reason === "exhausted") {
    title = "Your program is complete";
    body = "You've used all 12 sessions — beautifully done. Contact the wellness team to continue; they'll give you a new access code to redeem below.";
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="entitlement-modal-title"
      data-testid="entitlement-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(10, 20, 40, 0.62)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-md bg-white shadow-2xl p-6 sm:p-8"
        style={{ border: "1px solid var(--iw-border)", borderRadius: "3px" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8"
          style={{ border: "1px solid var(--iw-border)", borderRadius: "2px", color: "var(--iw-primary)" }}
          aria-label="Close"
          data-testid="entitlement-modal-close"
        >
          <span aria-hidden="true" className="font-mono">✕</span>
        </button>

        {redeemed ? (
          <div data-testid="entitlement-redeem-success">
            <div className="iw-mono uppercase mb-1.5" style={eyebrowStyle}>Code redeemed</div>
            <h2 id="entitlement-modal-title" className="serif font-bold m-0" style={titleStyle}>
              {redeemed.sessions_added} sessions added
            </h2>
            <p className="text-sm mt-3 mb-6 leading-relaxed" style={{ color: "var(--iw-body)" }}>
              You now have <strong>{redeemed.sessions_remaining}</strong> sessions available, one per week.
              {redeemed.can_start ? " You can begin right now." : ` Your next session unlocks on ${fmtUnlockDate(redeemed.next_available_at)}.`}
            </p>
            {redeemed.can_start ? (
              <button className="iw-btn-primary w-full justify-center" onClick={onProceed} data-testid="entitlement-begin-btn">
                Begin my session <span aria-hidden="true">↗</span>
              </button>
            ) : (
              <button className="iw-btn-ghost w-full justify-center" onClick={onClose} data-testid="entitlement-done-btn">
                Done
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="iw-mono uppercase mb-1.5" style={eyebrowStyle}>
              {reason === "weekly_lock" ? "Weekly pacing" : "Access code"}
            </div>
            <h2 id="entitlement-modal-title" className="serif font-bold m-0" style={titleStyle} data-testid="entitlement-modal-title">
              {title}
            </h2>
            <p className="text-sm mt-3 mb-5 leading-relaxed" style={{ color: "var(--iw-body)" }} data-testid="entitlement-modal-body">
              {body}
            </p>
            {showRedeem && (
              <form onSubmit={redeem}>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  required
                  placeholder="IW-XXXX-XXXX"
                  className="w-full px-4 py-3 bg-white text-base outline-none iw-mono tracking-widest"
                  style={{ border: "1px solid var(--iw-border)", borderRadius: "2px", color: "var(--iw-primary)" }}
                  data-testid="entitlement-code-input"
                />
                {error && (
                  <p className="text-sm mt-3 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="entitlement-code-error">
                    {error}
                  </p>
                )}
                <button type="submit" className="iw-btn-primary w-full mt-4 justify-center" disabled={busy} data-testid="entitlement-redeem-btn">
                  {busy ? "Checking…" : "Redeem code"} <span aria-hidden="true">↗</span>
                </button>
              </form>
            )}
            {reason === "weekly_lock" && (
              <button className="iw-btn-ghost w-full justify-center" onClick={onClose} data-testid="entitlement-ok-btn">
                Got it
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const eyebrowStyle = { color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 };
const titleStyle = { color: "var(--iw-primary)", fontSize: "22px" };
